import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { useUserContext } from "../../../contexts/userContext";

import {
  BuildingFloorItem,
  BuildingSpaceItem,
  NearBuildingItem,
  getBuildingInfo,
} from "../../../services/buildingServices";
import {
  SpaceItem,
  getMemberSpaceList,
  updateMemberSpace,
} from "../../../services/memberServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonButton from "../../../components/Button";
import HorizontalRule from "../../../components/Rule/Horizontal";
import BuildingFloorSelector from "../../../components/BuildingFloorSelector";
import BottomButtonGroup from "../../../components/Button/Bottom";
import SimplePopup from "../../../components/Popup/Simple";
import { Reason } from "../../../types/location";

type Props = {};

export default function MyInfoChangeBuilding(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, { updateField }] = useUserContext();
  const [selectedBuilding, setSelectedBuilding] = useState<NearBuildingItem>();
  const [selectedFloor, setSelectedFloor] = useState<BuildingFloorItem>();
  const [selectedSpace, setSelectedSpace] = useState<BuildingSpaceItem>();
  const [spaceList, setSpaceList] = useState<SpaceItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popup, setPopup] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const reason = Reason.BUILDING_CHANGE;

  const isNotEqual = (
    value1: string | undefined | null,
    value2: string | undefined | null
  ) => {
    return (value1 ?? "") !== (value2 ?? "");
  };

  const loadSpaceList = async () => {
    try {
      const res = await getMemberSpaceList();
      if (res.status === HttpStatusCode.Ok) {
        setSpaceList(res.data.slice(0, 5));
      }
    } catch (e: any) {}
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.allSettled([loadSpaceList()]).finally(() => setIsLoading(false));
    setIsLoading(false);
  }, []);

  return (
    <div className="min-h-content flex flex-col justify-between z-30">
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="근무 위치 변경"
          onBackClick={
            location.state?.invalid
              ? undefined
              : () => {
                  if (location.state?.goBack) navigate(-1);
                  else navigate("/myinfo");
                }
          }
        />
        <div className=" text-3xl whitespace-pre-line font-light pt-5">
          {"근무 위치 변경을\n도와드릴게요"}
        </div>
        <HorizontalRule margin="30px" />
        <BuildingFloorSelector
          selectedBuilding={selectedBuilding}
          selectedFloor={selectedFloor}
          selectedSpace={selectedSpace}
          onChangeBuilding={(building) => {
            setSelectedBuilding(building);
          }}
          onChangeFloor={(floor) => setSelectedFloor(floor)}
          onChangeSpace={(space) => setSelectedSpace(space)}
          showSpace
          unUseRecommend
          onBackHistory={() => {
            navigate("/myinfo");
          }}
          reason={reason}
        />
        <HorizontalRule className="bg-black !my-[33px]" />

        <div className=" text-lg font-medium pb-5">최근 선택한 근무위치</div>
        {spaceList.map((item, index) => (
          <div key={index}>
            <HorizontalRule margin="0px" />
            <div className="h-[58px] flex justify-between items-center">
              <div>
                {`${item.buildingName} ${item.buildingFloorName}${
                  item.buildingSpaceName ? ` - ${item.buildingSpaceName}` : ""
                }`}
              </div>
              {(!selectedBuilding ||
                selectedBuilding?.buildingId !== item.buildingId ||
                selectedFloor?.buildingFloorId !== item.buildingFloorId ||
                isNotEqual(
                  selectedSpace?.buildingSpaceId,
                  item.buildingSpaceId
                )) &&
                (selectedBuilding ||
                  user.buildingId !== item.buildingId ||
                  user.buildingFloorId !== item.buildingFloorId ||
                  isNotEqual(user.buildingSpaceId, item.buildingSpaceId)) && (
                  <CommonButton
                    variant="info"
                    className="p-2.5 text-xs font-medium"
                    onClick={async () => {
                      const res = await getBuildingInfo({
                        buildingId: item.buildingId,
                      });
                      setSelectedBuilding({
                        buildingId: item.buildingId,
                        buildingName: item.buildingName,
                        address: res.data.address,
                      } as NearBuildingItem);
                      setSelectedFloor({
                        buildingFloorId: item.buildingFloorId,
                        floorName: item.buildingFloorName,
                      } as BuildingFloorItem);
                      setSelectedSpace({
                        buildingSpaceId: item.buildingSpaceId,
                        spaceName: item.buildingSpaceName,
                      } as BuildingSpaceItem);
                    }}
                  >
                    선택
                  </CommonButton>
                )}
            </div>
          </div>
        ))}
      </div>

      <BottomButtonGroup
        title="근무 위치 변경"
        validation={!selectedBuilding || !selectedFloor}
        onClick={async () => {
          if (!selectedBuilding) {
            open("빌딩을 선택해주세요.");
            return;
          }

          
          if (!selectedFloor) {
            open("층을 선택해주세요.");
            return;
          }

          if (
            selectedBuilding.buildingId === user.buildingId &&
            selectedFloor.buildingFloorId === user.buildingFloorId && 
            selectedSpace?.buildingSpaceId === user.buildingSpaceId
          ) {
            open("현재 근무 위치와 동일합니다.");
            return;
          }

          setIsLoading(true);
          try {
            const result = await updateMemberSpace({
              buildingId: selectedBuilding?.buildingId as string,
              buildingFloorId: selectedFloor?.buildingFloorId as string,
              buildingSpaceId: selectedSpace?.buildingSpaceId as string,
            });
            if (result.status === HttpStatusCode.Ok) {
              setPopup("success");
            } else {
              setErrorMessage("근무 위치 변경에 실패하였습니다.");
              setPopup("fail");
            }
          } catch (e: any) {
            if (e?.response?.data?.error) {
              setErrorMessage(e.response.data.error?.replace(". ", ".\n"));
              setPopup("fail");
            } else {
              open("근무 위치 변경에 실패하였습니다.");
            }
          } finally {
            setIsLoading(false);
          }
        }}
      />
      <SimplePopup
        isOpen={popup === "success"}
        message="근무 위치가 변경되었습니다."
        onClose={() => {
          setPopup("");
          if (location.state?.goBack) {
            navigate(-1);
          } else {
            navigate("/myinfo");
          }
        }}
      />
      <SimplePopup
        isOpen={popup === "fail"}
        message={errorMessage}
        onClose={() => {
          setPopup("");
          navigate("/myinfo/change/building", {
            state: { invalid: location.state?.invalid },
          });
        }}
      />
      {simplePopupComponent}
    </div>
  );
}
